// Protoboard en 3D: cuerpo blanco con canal central, agujeros, rieles y numeración.
import * as THREE from 'three';
import { Builder, mat, canvas, texture, text } from '../kit.js';
import { BB_H, BB_ROWS, RAILS, bbSize, rowZ, colX, railHoles } from '../../parts/breadboard.js';

const texCache = new Map();

function topTexture(sizeKey, size) {
  if (texCache.has(sizeKey)) return texCache.get(sizeKey);
  const { w, d, cols } = size;
  const { c, g } = canvas(w, d, 14);
  const X = (x) => x + w / 2;
  const Y = (z) => z + d / 2;
  g.fillStyle = '#efede6';
  g.fillRect(0, 0, w, d);
  // Canal central (transparente: ahí se ve la ranura de la geometría)
  g.clearRect(0, Y(-1.35), w, 2.7);
  const hole = (x, z) => {
    g.fillStyle = '#d3d0c6';
    g.fillRect(X(x) - 0.72, Y(z) - 0.72, 1.44, 1.44);
    g.fillStyle = '#34343a';
    g.fillRect(X(x) - 0.52, Y(z) - 0.52, 1.04, 1.04);
    g.fillStyle = '#0d0d0f';
    g.fillRect(X(x) - 0.34, Y(z) - 0.34, 0.68, 0.68);
  };
  for (let col = 1; col <= cols; col++) for (let r = 0; r < 10; r++) hole(colX(col, cols), rowZ(r));
  if (size.groups) {
    const holes = railHoles(size);
    for (const rail of RAILS) for (const h of holes) hole(h.x, rail.z);
    const xa = holes[0].x - 2.2;
    const xb = holes[holes.length - 1].x + 2.2;
    for (const rail of RAILS) {
      const outer = rail.id === 'tn' || rail.id === 'bn';
      const lineZ = rail.z + (rail.id[0] === 't' ? (outer ? -1.75 : 1.75) : outer ? 1.75 : -1.75);
      g.fillStyle = rail.sign === '+' ? '#d0312d' : '#2a56c6';
      g.fillRect(X(xa), Y(lineZ) - 0.22, xb - xa, 0.44);
      for (const x of [xa - 2.2, xb + 2.2]) text(g, rail.sign === '+' ? '+' : '−', X(x), Y(rail.z), { size: 2.6, color: rail.sign === '+' ? '#c62d2a' : '#2a56c6', weight: 700 });
    }
  }
  for (let r = 0; r < 10; r++) {
    for (const x of [colX(1, cols) - 2.9, colX(cols, cols) + 2.9]) text(g, BB_ROWS[r], X(x), Y(rowZ(r)), { size: 1.5, color: '#8d8a80', weight: 700 });
  }
  for (let col = 1; col <= cols; col++) {
    if (col !== 1 && col % 5 !== 0) continue;
    text(g, String(col), X(colX(col, cols)), Y(rowZ(0)) - 2.0, { size: 1.35, color: '#8d8a80', weight: 700 });
    text(g, String(col), X(colX(col, cols)), Y(rowZ(9)) + 2.0, { size: 1.35, color: '#8d8a80', weight: 700 });
  }
  const t = texture(c);
  t.userData.shared = true;
  texCache.set(sizeKey, t);
  return t;
}

export function buildBreadboard(def, props) {
  const size = bbSize(props);
  const { w, d } = size;
  const b = new Builder();
  const body = mat('#ecebe4', { roughness: 0.72 });
  const halfD = (d - 2.7) / 2;
  b.rbox(w, BB_H - 0.05, halfD, 0.6, body, 0, 0, -(1.35 + halfD / 2));
  b.rbox(w, BB_H - 0.05, halfD, 0.6, body, 0, 0, 1.35 + halfD / 2);
  b.box(w - 1, BB_H - 3.5, 2.8, mat('#d8d6cd', { roughness: 0.8 }), 0, 0, 0);
  b.build();
  const tex = topTexture(props.size || 'half', size);
  const top = new THREE.Mesh(
    new THREE.PlaneGeometry(w - 0.4, d - 0.4).rotateX(-Math.PI / 2),
    new THREE.MeshStandardMaterial({ map: tex, roughness: 0.75, alphaTest: 0.5 }),
  );
  tex.repeat.set(1, 1);
  top.position.y = BB_H + 0.005;
  top.receiveShadow = true;
  b.root.add(top);
  return { root: b.root };
}
